import { Modal, Box, TextField, Button, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router";

import { makeStyles } from "../../../../theme/theme";
import TSVideoClipper from "../../TSVideoPlayer/TSVideoClipper";
import TSVideoPlayer from "../../TSVideoPlayer/TSVideoPlayer";
import type { RootState } from "../../../../store/state/rootReducer";
import {
  updateModalTitle,
  updateModalDesc,
  updateModalTags,
  updateModalStart,
  updateModalEnd,
} from "../../../../store/state/story_authoring/actions";

import UpdateClipButton from "./updateClipButton";

const useStyles = makeStyles()(() => ({
  modalBox: {
    background: "white",
    width: "80%",
    minWidth: "900px",
    margin: "5% auto",
    padding: "24px",
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: "bold",
  },
  textField: {
    width: "100%",
    marginBottom: "20px",
  },
}));

interface ClipModalProps {
  showModal: boolean;
  isCreating?: boolean;
  onDelete?: () => void;
  isViewOnly?: boolean;
  storyId?: string;
  onClose: (show: boolean) => void;
  onSave?: (clipName: any, clipDesc: any, clipStart: any, clipEnd: any, clipKeywords: any) => void;
}

const ClipModal = ({ showModal, isCreating, onDelete, isViewOnly, storyId, onClose, onSave }: ClipModalProps) => {
  const { classes } = useStyles();
  const { videoUrl, description, start, end, tags, title, storyDuration, clipFragmentUrl, isAudioFile } = useSelector(
    (s: RootState) => s.storyAuthoring.clipModal
  );
  const difference = Number(end) - Number(start);
  const unAbleToSave = end < start || start === end || difference < 2;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showGoToInterviewButton = useLocation().pathname.endsWith("/clips");

  const onCancel = () => {
    onClose(false);
  };

  return (
    <Modal
      open={showModal}
      onClose={onCancel}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box className={classes.modalBox}>
        <Box paddingBottom="24px">
          {isCreating ? (
            <h2 className={classes.modalTitle}>Create Clip</h2>
          ) : (
            <h2 className={classes.modalTitle}>{isViewOnly ? "Preview Clip" : "Edit Clip"}</h2>
          )}
        </Box>
        <Box sx={{ width: "100%" }}>
          <Box display="flex" justifyContent={"space-between"} columnGap="24px">
            <Box width="50%">
              <TextField
                onChange={(e) => dispatch(updateModalTitle({ clipTitle: e.target.value }))}
                className={classes.textField}
                id="clipTitle"
                label="Title"
                variant="outlined"
                disabled={isViewOnly}
                value={title}
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#000000",
                  },
                }}
              />
              <TextField
                className={classes.textField}
                onChange={(e) => dispatch(updateModalDesc({ clipDesc: e.target.value }))}
                label="Description"
                multiline={true}
                minRows={2}
                maxRows={5}
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#000000",
                  },
                  "& textarea::-webkit-scrollbar": {
                    width: "10px",
                  },
                  "& textarea::-webkit-scrollbar-thumb": {
                    background: "#606971",
                    borderRadius: "10px",
                    outline: "none",
                  },
                  "& textarea::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                }}
                disabled={isViewOnly}
                value={description}
              />

              <TextField
                helperText={"Enter words separated by commas"}
                className={classes.textField}
                onChange={(e) => dispatch(updateModalTags({ clipKeywords: e.target.value }))}
                label="Keywords"
                disabled={isViewOnly}
                value={tags}
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#000000",
                  },
                }}
              />
            </Box>
            <Box width="50%">
              {isViewOnly ? (
                <TSVideoPlayer videoSrc={clipFragmentUrl} isAudioFile={isAudioFile} />
              ) : (
                <>
                  <TSVideoClipper
                    videoSrc={videoUrl}
                    startingSecond={Number(start)}
                    endingSecond={Number(end)}
                    videoDuration={storyDuration}
                    setClipStartToSave={(value) => dispatch(updateModalStart({ clipStart: value }))}
                    setClipEndToSave={(value) => dispatch(updateModalEnd({ clipEnd: value }))}
                  />
                  <Box sx={{ paddingTop: "8px", display: "flex", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", gap: "4px" }}>
                      <UpdateClipButton onClick={() => dispatch(updateModalStart({ clipStart: Number(start) - 0.1 }))}>
                        -0.1"
                      </UpdateClipButton>
                      <UpdateClipButton onClick={() => dispatch(updateModalStart({ clipStart: Number(start) + 0.1 }))}>
                        +0.1"
                      </UpdateClipButton>
                    </Box>
                    <Box sx={{ display: "flex", gap: "4px" }}>
                      <UpdateClipButton onClick={() => dispatch(updateModalEnd({ clipEnd: Number(end) - 0.1 }))}>
                        -0.1"
                      </UpdateClipButton>
                      <UpdateClipButton onClick={() => dispatch(updateModalEnd({ clipEnd: Number(end) + 0.1 }))}>
                        +0.1"
                      </UpdateClipButton>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Box>
          <Box display="flex" marginTop="24px" justifyContent="space-between">
            <Box>
              {showGoToInterviewButton && (
                <Button
                  sx={{
                    textTransform: "none",
                    fontWeight: "bold",
                    ":hover": { color: "#239B8B" },
                    "&:focus": {
                      outline: "none",
                    },
                  }}
                  onClick={() => {
                    navigate(`/myInterviews/stories/${storyId}/hub?start=${start}&end=${end}`);
                  }}
                >
                  Go to interview
                </Button>
              )}
              {isViewOnly && (
                <Button
                  sx={{
                    textTransform: "none",
                    fontWeight: "bold",
                    ":hover": { color: "#239B8B" },
                    "&:focus": {
                      outline: "none",
                    },
                  }}
                  disabled={!clipFragmentUrl}
                  href={clipFragmentUrl}
                >
                  Download
                </Button>
              )}
              {!isCreating && !isViewOnly && (
                <Button
                  sx={{
                    textTransform: "none",
                    fontWeight: "bold",
                    "&:hover": {
                      outline: "none",
                    },
                    "&:active": {
                      outline: "none",
                    },
                    "&:focus": {
                      outline: "none",
                    },
                  }}
                  onClick={onDelete}
                >
                  Delete
                </Button>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                columnGap: "8px",
                "&:hover": {
                  outline: "none",
                },
                "&:active": {
                  outline: "none",
                },
                "&:focus": {
                  outline: "none",
                },
              }}
            >
              <Button
                onClick={() => {
                  onCancel();
                }}
                variant={isViewOnly ? "contained" : "outlined"}
                sx={{
                  textTransform: "none",
                  fontWeight: "bold",
                  "&:focus": {
                    outline: "none",
                  },
                }}
              >
                {!isViewOnly ? "Cancel" : "Close"}
              </Button>
              {!isViewOnly && (
                <Tooltip title={"Clips should be at least 2 seconds long"} disableHoverListener={!unAbleToSave}>
                  <span>
                    <Button
                      variant="contained"
                      onClick={() => {
                        onSave(title, description, start, end, tags);
                      }}
                      sx={{
                        textTransform: "none",
                        fontWeight: "bold",
                        "&:focus": {
                          outline: "none",
                        },
                      }}
                      disabled={unAbleToSave}
                    >
                      Save
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ClipModal;
